import * as React from 'react';

import { IconButton, Paper } from '@mui/material';
import { Menu, Search } from 'lucide-react';

import { useSidebarState } from '@app/components/sidebar/SidebarStateProvider';
import { GlobalSearch } from '@app/components/topbar/GlobalSearch';
import { LanguageMenu } from '@modules/_shared/components/LanguageMenu';
import { useTopbar } from '@modules/_shared/components/topbar/Topbar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { NotificationBell } from '@modules/notification-inbox/NotificationBell';
import { DocumentTitle } from '@shared/components/documentTitle';
import { useLocale } from '@shared/hooks/locale';
import { useNavigation } from '@shared/NavigationContext';
import {
  MEDIA_TABLET,
  TOP_BAR_HEIGHT,
  pageTitleTypographySx,
} from '@shared/theme/layout';
import { useTheme } from '@shared/theme/theme';

const isMac =
  typeof navigator !== 'undefined' && /Macintosh/.test(navigator.userAgent);
const shortcutKey = isMac ? '⌘K' : 'Ctrl+K';

// Hook to detect if device has keyboard capabilities
const useHasKeyboard = () => {
  const [hasKeyboard, setHasKeyboard] = React.useState(false);

  React.useEffect(() => {
    // Check if device has keyboard capabilities
    const checkKeyboard = () => {
      // Most reliable way to detect if device has keyboard
      const hasKeyboardCapability =
        typeof navigator !== 'undefined' &&
        (navigator.maxTouchPoints === 0 ||
          (navigator.maxTouchPoints > 0 &&
            window.matchMedia('(pointer: fine)').matches));

      setHasKeyboard(hasKeyboardCapability);
    };

    checkKeyboard();
  }, []);

  return hasKeyboard;
};

type Props = {
  extra?: React.ReactNode;
};

const TopPanel = ({
  position,
  children,
}: {
  position?: Array<'static' | 'sticky'>;
  children: React.ReactNode;
}) => {
  const { depth } = useTheme();
  const [base, tablet] = position ?? ['static', 'sticky'];
  return (
    <div
      css={{
        flexShrink: 0,
        zIndex: depth.appBar,
        top: 0,
        position: base,
        ...(tablet != null && { [MEDIA_TABLET]: { position: tablet } }),
      }}
    >
      {children}
    </div>
  );
};

const LeftAction = () => {
  const { t } = useLocale();
  const { openMobile } = useSidebarState();
  return (
    <div css={{ marginLeft: 4 }}>
      <IconButton
        color="inherit"
        title={t('openSideMenu')}
        onClick={openMobile}
      >
        <Menu size={20} />
      </IconButton>
    </div>
  );
};

export const Navigation = (props: Props) => {
  const { text, colors } = useTheme();
  const state = useNavigation();
  const [search, setSearch] = React.useState(false);
  const { titleRef } = useTopbar();
  const { isMobile } = useSidebarState();
  const hasKeyboard = useHasKeyboard();

  React.useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setSearch(true);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setSearch(false);
      }
    };
    document.addEventListener('keydown', focusSearch);
    return () => document.removeEventListener('keydown', focusSearch);
  }, []);

  const { me } = useAppData();

  return (
    <TopPanel position={state.position}>
      <Paper
        square={true}
        elevation={0}
        css={{
          position: 'relative',
          zIndex: 2,
          background: colors.white,
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            minHeight: TOP_BAR_HEIGHT,
            zIndex: 8,
            borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          <div css={{ flexShrink: 0 }}>
            {search === false && isMobile && <LeftAction />}
          </div>

          <DocumentTitle>
            {state.title != null
              ? `${state.title} | RealAdvisor CRM`
              : 'RealAdvisor CRM'}
          </DocumentTitle>

          {!search && state.level0 != null && state.level0}

          {!search && state.title != null && (
            <div
              css={[
                { minWidth: 0, marginLeft: 8, flexGrow: 1 },
                text.h6,
                text.truncate(1),
                // bold + tighter on phones; full h6 from tablet up
                pageTitleTypographySx,
                { [MEDIA_TABLET]: { marginLeft: 16 } },
              ]}
            >
              {state.title ?? null}
            </div>
          )}

          {state.title == null && (
            <div
              ref={titleRef}
              css={[
                { minWidth: 0, marginLeft: 8, flexGrow: 1 },
                { display: search === false ? 'block' : 'none' },
                text.h6,
                text.truncate(1),
                // bold + tighter on phones; full h6 from tablet up
                pageTitleTypographySx,
                { [MEDIA_TABLET]: { marginLeft: 16 } },
              ]}
            />
          )}

          {search && <GlobalSearch onClose={() => setSearch(false)} />}

          <div
            hidden={search}
            css={{
              display: 'flex',
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <div css={{ position: 'relative' }}>
              <IconButton
                color="inherit"
                title="search"
                onClick={() => {
                  setSearch(true);
                }}
              >
                <Search size={22} strokeWidth={1.8} />
              </IconButton>
              <div
                css={{
                  position: 'absolute',
                  bottom: -3,
                  right: -3,
                  fontSize: '0.55rem',
                  color: colors.gray500,
                  background: colors.gray100,
                  padding: '0px 3px',
                  borderRadius: 2,
                  fontWeight: 'bold',
                  display: hasKeyboard ? 'block' : 'none',
                }}
              >
                {shortcutKey}
              </div>
            </div>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                paddingRight: 4,
              }}
            >
              {me && <NotificationBell />}
              {me && <LanguageMenu />}
            </div>
            {props.extra}
          </div>
        </div>
        {state.level1}
      </Paper>
    </TopPanel>
  );
};
